/**
 * Pareja · «Ya no estás a bordo»: cuando el capitán te baja de un presupuesto
 * (o te bajas tú), su copia se quita de este aparato y se explica qué ha
 * pasado en vez de dar un error de sincronización.
 */
import { useSyncExternalStore } from 'react';

import { send } from '@actual-app/core/platform/client/connection';
import type { File } from '@actual-app/core/types/file';

export type FueraDelBarco = {
  presupuesto: string;
  capitan: string | null;
};

const CLAVE = 'rumbo-fuera-del-barco';

let actual: FueraDelBarco | null = leer();
const oyentes = new Set<() => void>();

function leer(): FueraDelBarco | null {
  try {
    const texto = sessionStorage.getItem(CLAVE);
    return texto ? (JSON.parse(texto) as FueraDelBarco) : null;
  } catch {
    return null;
  }
}

function poner(valor: FueraDelBarco | null) {
  actual = valor;
  try {
    if (valor) {
      sessionStorage.setItem(CLAVE, JSON.stringify(valor));
    } else {
      sessionStorage.removeItem(CLAVE);
    }
  } catch {
    // Sin almacenamiento: vale con la memoria (no sobrevive a recargar).
  }
  oyentes.forEach(oyente => oyente());
}

export function cerrarFueraDelBarco() {
  poner(null);
}

export function useFueraDelBarco(): FueraDelBarco | null {
  return useSyncExternalStore(
    oyente => {
      oyentes.add(oyente);
      return () => oyentes.delete(oyente);
    },
    () => actual,
  );
}

/** El nombre del presupuesto y de su capitán, de la lista de archivos. */
export function datosDelBarco(
  archivos: readonly File[],
  localId: string,
): FueraDelBarco {
  const archivo = archivos.find(f => 'id' in f && f.id === localId);
  const presupuesto = archivo?.name ?? '';
  if (!archivo || !('usersWithAccess' in archivo)) {
    return { presupuesto, capitan: null };
  }
  const capitan = archivo.usersWithAccess.find(u => u.owner);
  return {
    presupuesto,
    capitan: capitan
      ? (capitan.displayName || capitan.userName.split('@')[0]).trim() || null
      : null,
  };
}

/**
 * Quita de este aparato la copia de un presupuesto al que ya no se va a
 * bordo y enseña la pantalla que lo explica. Solo se borra la copia local
 * (nunca se pasa el id de la nube: el presupuesto del capitán sigue intacto).
 */
export async function quitarCopiaDelBarco(
  localId: string,
  datos: FueraDelBarco,
) {
  await send('delete-budget', { id: localId });
  poner(datos);
}
